import Markers from './index'
import MarkerForm from './components/MarkerForm'
import WidgetActionMenu from './components/WidgetActionMenu'
import WidgetActionsMenuService from 'live/services/widget-actions-menu'
import widgetService, { LifeCycleEventContext } from 'live/services/widgets'
import LiveAPi from 'live/lib/api'
import { bufferTime, filter, map, mergeAll } from 'rxjs/operators'
import { orderBy } from 'lodash'
import ChartMarker from './visual/chart-marker'
import Logger from 'live/services/logger'
import { MarkersData, QueryParams } from './api'

// Keep ref to be able to clean up any resources on destroy
const observingWidgets: { [widgetId: string]: () => void } = {}

const logger = Logger('plugin-annotations markers registry')

/**
 * Register a widget to have marker capabilities.
 *
 * @param widgetType Widget that will have support for markers.
 * @param test A function to test if `beforeListMarker` and `beforeListMarker` should be called.
 * @param beforeSaveMarker Function to call before saving the marker.
 * @param beforeListMarker Function to call before querying the marker.
 *
 * @returns void
 */
export default function registryWidget(
    widgetType: string,
    test?: (data: MarkersData | QueryParams, widget: unknown, config: Record<string, unknown>) => boolean,
    beforeSaveMarker?: (marker: MarkersData, widget: unknown) => Promise<MarkersData>,
    beforeListMarker?: (query: QueryParams, widget: unknown) => QueryParams
): void {
    if (!LiveAPi.Extension.widget.adjacentComponent || !widgetService.lifeCycleObserver) {
        console.warn('Expected live-ui API not found, probably running older than required Live version')

        return
    }

    LiveAPi.Extension.widget.adjacentComponent.register(MarkerForm)

    if (beforeListMarker && beforeSaveMarker) {
        Markers.registerSaveAndListStrategyFor(widgetType, test, beforeSaveMarker, beforeListMarker)
    }

    const Action = { component: WidgetActionMenu, widgetType }
    WidgetActionsMenuService.addToGroup(Action, 'tools')

    getObserver(widgetType, 100)
        .subscribe((ctx: LifeCycleEventContext) => {
                const { widget, event } = ctx
                let widgetResource = ctx.widgetResource

                if (!widget) {
                    logger.warn('Unexpected undefined widget', ctx)
                    return
                }

                if (!widgetResource) {
                    widgetResource = widget.options
                }

                if (!widgetResource) {
                    logger.warn('Unexpected undefined widgetResource or widget.options', ctx)
                    return
                }

                if (event == 'afterReady') {
                    onWidgetInit(ctx)

                } else if (event == 'beforeDestroy') {
                    onWidgetDestroy(widgetResource?.id)
                } else if (event == 'afterQueryStart') {

                    // Out of order: `afterReady` should come first
                    if (!observingWidgets[widgetResource?.id]) return

                    const axis = ChartMarker.getXAxis(widget.chart)

                    // live-ui can remove plot lines by clearing them from the highcharts instance
                    // current strategy is track expected number of markers x actual number of visual markers
                    // we need to force re-do query in this case.
                    const plotLinesWereRemoved = widget.chart.__annotation_markers__ && axis?.plotLinesAndBands && axis.plotLinesAndBands.length < widget.chart.__annotation_markers__

                    if (plotLinesWereRemoved) {
                        onWidgetDestroy(widgetResource?.id)
                        onWidgetInit(ctx)
                    } else {
                        Markers.maybeUpdateQueryFor(widget)
                    }
                }

            }
        )
}

function onWidgetDestroy(id: number): void {
    if (observingWidgets[id]) {
        observingWidgets[id]()
        delete observingWidgets[id]
    }
}

function onWidgetInit({ widgetResource, widget }: LifeCycleEventContext): void {
    if (!observingWidgets[widgetResource.id]) {
        observingWidgets[widgetResource.id] = Markers.assemble(widget)

        // reset expected counter
        if (widget.chart) {
            widget.chart.__annotation_markers__ = 0
        }
    }
}


/**
 * debounced observer to help avoid making multiple marker-query requests.
 *
 * @param widgetType
 * @param debounceTime
 */
function getObserver(widgetType, debounceTime) {
    return widgetService.lifeCycleObserver(widgetType)
        .pipe(
            bufferTime(debounceTime),

            // ignore if empty because bufferTime emits periodically
            filter(ar => ar.length > 0),

            // ignore consecutive duplicate events
            map((calls: LifeCycleEventContext[]) => {
                return orderBy(calls, item => item.widgetResource.id) // order by widget id, to filter consecutive events per widget
                    .reduce((result, item) => {
                        const prev = result.length > 0 ? result[result.length - 1] : null
                        if (!prev || item.event != prev.event || item.widgetResource.id != prev.widgetResource.id) {
                            result.push(item)
                        }
                        return result
                    }, [])
            }),

            mergeAll()
        )
}


